import {
  conferenceGalleries,
  corporateRetreatGalleries,
  holidayPartyGalleries,
} from '@/data/adventureGalleries';
import { tagRegistry } from '@/data/reckonWayTags';
import { reckonBallEvents } from '@/content/reckon-ball';
import searchUtilities from '@/lib/reckonWaySearch.cjs';
import { experiences } from './experiences';
import { traditions } from './traditions';

const { filterEntries, getFacetState } = searchUtilities;

const supplementalTagDefinitions = [
  { slug: 'axe-throwing', label: 'Axe Throwing', category: 'experience' },
  { slug: 'competition', label: 'Competition', category: 'experience' },
  { slug: 'food-and-drink', label: 'Food & Drink', category: 'experience' },
  { slug: 'happy-hour', label: 'Happy Hour', category: 'experience' },
  { slug: 'karaoke', label: 'Karaoke', category: 'experience' },
  { slug: 'lunch', label: 'Lunch', category: 'experience' },
  { slug: 'movies', label: 'Movies', category: 'experience' },
  { slug: 'private-dining', label: 'Private Dining', category: 'experience' },
  { slug: 'racing', label: 'Racing', category: 'experience' },
  { slug: 'string-quartet', label: 'String Quartet', category: 'experience' },
  { slug: 'team-lunch', label: 'Team Lunch', category: 'experience' },
  { slug: 'after-party', label: 'After Party', category: 'theme' },
  { slug: 'after-work', label: 'After Work', category: 'theme' },
  { slug: 'celebration', label: 'Celebration', category: 'theme' },
  { slug: 'destination-weekend', label: 'Destination Weekend', category: 'theme' },
  { slug: 'employees-and-guests', label: 'Employees & Guests', category: 'theme' },
  { slug: 'farewell-brunch', label: 'Farewell Brunch', category: 'theme' },
  { slug: 'gift-card-exchange', label: 'Gift Card Exchange', category: 'theme' },
  { slug: 'gift-exchange', label: 'Gift Exchange', category: 'theme' },
  { slug: 'indoor', label: 'Indoor', category: 'theme' },
  { slug: 'office-culture', label: 'Office Culture', category: 'theme' },
  { slug: 'overnight-stay', label: 'Overnight Stay', category: 'theme' },
  { slug: 'signature-toast', label: 'Signature Toast', category: 'theme' },
  { slug: 'team-connection', label: 'Team Connection', category: 'theme' },
  { slug: 'welcome', label: 'Welcome', category: 'theme' },
  { slug: 'annual-tradition', label: 'Annual Tradition', category: 'collection' },
  { slug: 'company-anniversary', label: 'Company Anniversary', category: 'collection' },
  { slug: 'end-of-year', label: 'End of Year', category: 'collection' },
  { slug: 'local-event', label: 'Local Event', category: 'collection' },
  { slug: 'new-employee', label: 'New Employee', category: 'collection' },
  { slug: 'reckon-ball', label: 'Reckon Ball', category: 'collection' },
  { slug: 'weekly-tradition', label: 'Weekly Tradition', category: 'collection' },
  { slug: 'washington-dc', label: 'Washington, D.C.', category: 'destination' },
];

const tagDefinitionsBySlug = [...tagRegistry, ...supplementalTagDefinitions]
  .reduce((lookup, tag) => ({ ...lookup, [tag.slug]: tag }), {});

function tagSlugs(tags = []) {
  return [...new Set(tags
    .map((tag) => (typeof tag === 'string' ? tag : tag?.slug))
    .filter(Boolean))];
}

function destinationLabel(destination = {}) {
  return [destination.city, destination.region, destination.country]
    .filter(Boolean)
    .filter((value, index, values) => values.indexOf(value) === index)
    .join(', ');
}

function normalizedDate(value) {
  if (!value) return '';

  const exactDate = String(value).match(/\d{4}-\d{2}-\d{2}/);
  if (exactDate) return exactDate[0];

  const year = String(value).match(/\b(19|20)\d{2}\b/);
  return year ? `${year[0]}-01-01` : '';
}

function latestKnownDate(values = []) {
  return values
    .map(normalizedDate)
    .filter(Boolean)
    .sort((a, b) => b.localeCompare(a))[0] || '';
}

function galleryEntry(gallery, kind, kindSlug) {
  return {
    id: `gallery:${gallery.slug}`,
    kind,
    kindSlug,
    title: gallery.title,
    summary: gallery.summary || gallery.description || gallery.subtitle,
    image: gallery.coverImage || gallery.heroImage,
    route: { name: 'reckon-way-gallery', params: { gallerySlug: gallery.slug } },
    metadata: [
      gallery.dateLabel,
      destinationLabel(gallery.destination),
      gallery.tripType,
    ].filter(Boolean),
    tagSlugs: tagSlugs(gallery.tags),
    sortDate: latestKnownDate([gallery.endDate, gallery.startDate, gallery.dateLabel]),
  };
}

function traditionRoute(tradition) {
  return tradition.archiveRouteName
    ? { name: tradition.archiveRouteName }
    : { name: 'reckon-way-tradition', params: { traditionSlug: tradition.slug } };
}

function traditionEntry(tradition) {
  return {
    id: `tradition:${tradition.id}`,
    kind: 'Tradition',
    kindSlug: 'tradition',
    title: tradition.name,
    summary: tradition.summary,
    image: tradition.heroImage,
    route: traditionRoute(tradition),
    metadata: [
      tradition.startDate ? `Since ${tradition.startDate.slice(0, 4)}` : '',
      tradition.occurrenceCount ? `${tradition.occurrenceCount} occurrences` : '',
    ].filter(Boolean),
    tagSlugs: tagSlugs(tradition.tags),
    sortDate: latestKnownDate([
      ...(tradition.occurrences || []).map((occurrence) => occurrence.date),
      ...(tradition.milestones || []).map((milestone) => milestone.date),
      tradition.startDate,
    ]),
  };
}

function experienceEntry(experience) {
  const location = [experience.place?.city, experience.place?.state]
    .filter(Boolean)
    .join(', ');

  return {
    id: `experience:${experience.id}`,
    kind: 'Local Outing',
    kindSlug: 'local-outing',
    title: experience.name,
    summary: experience.summary,
    image: experience.heroImage,
    route: { name: 'reckon-way-experience', params: { tagSlug: experience.slug } },
    metadata: [experience.date?.slice(0, 4), location || experience.place?.name]
      .filter(Boolean),
    tagSlugs: tagSlugs(experience.tags),
    sortDate: normalizedDate(experience.date),
  };
}

function galleryYear(gallery) {
  const match = gallery.slug.match(/(\d{4})$/);
  return match ? Number(match[1]) : null;
}

const holidayGalleriesByYear = holidayPartyGalleries.reduce((lookup, gallery) => ({
  ...lookup,
  [galleryYear(gallery)]: gallery,
}), {});

function reckonBallEntry(event) {
  const legacyGallery = holidayGalleriesByYear[event.year];
  const location = [event.location?.neighborhood, event.location?.city, event.location?.state]
    .filter(Boolean)
    .join(', ');

  return {
    id: `reckon-ball:${event.year}`,
    kind: 'Reckon Ball Event',
    kindSlug: 'reckon-ball-event',
    title: event.title,
    summary: event.hero.summary,
    image: event.coverImage || event.hero.image,
    route: { name: 'reckon-ball-year', params: { year: String(event.year) } },
    metadata: [String(event.year), location, event.dining?.restaurant].filter(Boolean),
    tagSlugs: tagSlugs([...(event.tags || []), ...(legacyGallery?.tags || [])]),
    sortDate: `${event.year}-12-31`,
  };
}

const galleryEntries = [
  ...corporateRetreatGalleries
    .map((gallery) => galleryEntry(gallery, 'Corporate Retreat', 'corporate-retreat')),
  ...conferenceGalleries.map((gallery) => galleryEntry(gallery, 'Conference', 'conference')),
];

export const reckonWaySearchEntries = [
  ...galleryEntries,
  ...traditions.map(traditionEntry),
  ...experiences.map(experienceEntry),
  ...reckonBallEvents.map(reckonBallEntry),
];

const usedTagSlugs = [...new Set(reckonWaySearchEntries.flatMap((entry) => entry.tagSlugs))];

export const reckonWaySearchTags = usedTagSlugs
  .map((slug) => tagDefinitionsBySlug[slug])
  .filter(Boolean);

export const reckonWaySearchKindSlugs = [...new Set(
  reckonWaySearchEntries.map((entry) => entry.kindSlug),
)];

export function filterReckonWaySearchEntries(filters = {}) {
  return filterEntries(reckonWaySearchEntries, filters);
}

export function getReckonWayFacetState(filters = {}) {
  return getFacetState(reckonWaySearchEntries, reckonWaySearchTags, filters);
}

function validateSearchIndex() {
  const errors = [];
  const entryIds = new Set();
  const tagCounts = {};

  reckonWaySearchEntries.forEach((entry) => {
    if (entryIds.has(entry.id)) errors.push(`Duplicate search entry ID: ${entry.id}`);
    entryIds.add(entry.id);
    if (!entry.title || !entry.kind || !entry.kindSlug || !entry.route) {
      errors.push(`Incomplete search entry: ${entry.id}`);
    }
    if (!entry.tagSlugs.length) errors.push(`Search entry has no tags: ${entry.id}`);
    if (entry.sortDate && !/^\d{4}-\d{2}-\d{2}$/.test(entry.sortDate)) {
      errors.push(`Invalid search sort date: ${entry.id}`);
    }

    entry.tagSlugs.forEach((slug) => {
      tagCounts[slug] = (tagCounts[slug] || 0) + 1;
      if (!tagDefinitionsBySlug[slug]) errors.push(`Missing tag metadata: ${slug}`);
    });
  });

  reckonWaySearchTags.forEach((tag) => {
    if (!tagCounts[tag.slug]) errors.push(`Search tag has no results: ${tag.slug}`);
  });

  reckonBallEvents.forEach((event) => {
    if (!holidayGalleriesByYear[event.year]) {
      errors.push(`Missing holiday gallery for Reckon Ball ${event.year}`);
    }
  });

  holidayPartyGalleries.forEach((gallery) => {
    const year = galleryYear(gallery);
    if (!reckonBallEvents.some((event) => event.year === year)) {
      errors.push(`Unmatched holiday gallery: ${gallery.slug}`);
    }
  });

  const fineDiningKaraokeResults = filterReckonWaySearchEntries({
    tagSlugs: ['fine-dining', 'karaoke'],
    matchMode: 'all',
  });
  if (fineDiningKaraokeResults.length !== 1 || fineDiningKaraokeResults[0].id !== 'reckon-ball:2025') {
    errors.push('Fine Dining + Karaoke must resolve only to the canonical 2025 Reckon Ball');
  }

  const incompatibleDestinationResults = filterReckonWaySearchEntries({
    tagSlugs: ['london', 'sydney'],
    matchMode: 'all',
  });
  if (incompatibleDestinationResults.length) {
    errors.push('London + Sydney must have no strict Match all results');
  }

  const compatibleDestinationResults = filterReckonWaySearchEntries({
    tagSlugs: ['london', 'sydney'],
    matchMode: 'any',
  });
  if (compatibleDestinationResults.length < 2) {
    errors.push('London + Sydney must broaden results under Match any');
  }

  const londonFacetState = getReckonWayFacetState({
    tagSlugs: ['london'],
    matchMode: 'all',
  });
  const sydneyOption = londonFacetState.tags.find((tag) => tag.slug === 'sydney');
  if (!sydneyOption || sydneyOption.compatible || sydneyOption.count !== 0) {
    errors.push('Sydney must become unavailable after selecting London under Match all');
  }

  return [...new Set(errors)];
}

export const reckonWaySearchValidationErrors = validateSearchIndex();

if (reckonWaySearchValidationErrors.length) {
  throw new Error(`Invalid Reckon Way search index:\n${reckonWaySearchValidationErrors.join('\n')}`);
}
